/**
 * El armazón del panel de administración: barra lateral, migas y contenido.
 *
 * La navegación se arma desde un solo arreglo. Agregar una pantalla es agregar
 * una línea acá y una ruta en `App.tsx`, y no hay dos listas que se puedan
 * desincronizar.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSession } from '@/lib/session';
import { useApi } from '@/lib/hooks';
import { Brandmark } from './common/Brandmark';
import { Icon, type IconName } from './common/NavIcons';
import { Avatar } from './ui';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Nombre del contador del panel que se muestra como globo. */
  badge?: 'submissions_pending' | 'charges_needing_review' | 'overdue' | 'obligations_overdue';
  /** Solo para `ADMIN`. Ver la nota de `SISTEMA → Configuración`. */
  adminOnly?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

/* Un solo destino para todo el ciclo de cobro.
   Antes eran cinco entradas sueltas —Generar mes, Cobros, Pagos, Facturación,
   Conciliación— que en realidad son etapas de lo mismo. Ahora es un workspace
   con subnavegación, y «Generar mes» es una acción dentro, no un lugar.

   «Contratos» tampoco es un destino: un contrato se edita desde su consultorio,
   que es donde uno lo va a buscar. La ruta sigue viva por compatibilidad. */
const NAV: NavGroup[] = [
  {
    label: 'Operación',
    items: [
      { to: '/admin', label: 'Panel', icon: 'panel' },
      { to: '/admin/clientes', label: 'Clientes', icon: 'clientes' },
      { to: '/admin/calendario', label: 'Calendario', icon: 'calendario' },
      { to: '/admin/solicitudes', label: 'Solicitudes', icon: 'solicitudes' },
      { to: '/admin/comunicacion', label: 'Comunicación', icon: 'comunicacion' },
    ],
  },
  {
    label: 'Cobros',
    items: [
      { to: '/admin/cobros', label: 'Cobros', icon: 'cobros', badge: 'submissions_pending' },
    ],
  },
  {
    label: 'Negocio',
    items: [
      { to: '/admin/finanzas', label: 'Finanzas', icon: 'finanzas' },
      /* Lo que la empresa paga, al lado de lo que la empresa cobra. El globo
         muestra lo vencido —no lo de esta semana—: un número permanente al lado
         del nombre deja de leerse a la semana. */
      { to: '/admin/obligaciones', label: 'Obligaciones', icon: 'obligaciones', badge: 'obligations_overdue' },
      { to: '/admin/inventario', label: 'Inventario', icon: 'inventario' },
      /* Entrada propia y no una pestaña adentro de Catálogo (decisión del
         dueño, P6.6). Va pegada a Inventario porque es de donde se llega:
         un proveedor se carga cuando hace falta comprarle algo. */
      { to: '/admin/proveedores', label: 'Proveedores', icon: 'proveedores' },
      { to: '/admin/personal', label: 'Personal', icon: 'personal' },
      { to: '/admin/calidad', label: 'Calidad', icon: 'calidad' },
      { to: '/admin/incidencias', label: 'Incidencias', icon: 'incidencias' },
      { to: '/admin/reportes', label: 'Reportes', icon: 'reportes' },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { to: '/admin/catalogo', label: 'Catálogo', icon: 'catalogo' },
      /* Configuración vuelve al sidebar (CB-21c). Estaba escondida en el menú
         del avatar, que era la ÚNICA forma de llegar: un admin nuevo no la
         encontraba nunca. Y no es una preferencia personal —organización,
         datos de pago, planilla, chips NFC, integraciones—, así que su lugar
         es acá, junto a Catálogo, que es donde ya vive lo configurable.

         `adminOnly` conserva la guarda que tenía en el menú. Hoy el Shell solo
         lo monta `AdminPage`, que ya exige ADMIN, así que la condición es
         redundante — y se deja igual: el día que este armazón sirva a otro rol,
         la entrada no tiene que aparecerle a quien no puede abrirla. */
      { to: '/admin/configuracion', label: 'Configuración', icon: 'configuracion', adminOnly: true },
    ],
  },
];

interface DashboardCounts {
  counts: Record<string, number>;
}

/* El mismo punto de corte que `app.css` (≤900px: drawer; ≥901px: barra fija).
   Se consulta desde JS porque `inert` es un atributo, no una regla de CSS: en
   escritorio la barra está SIEMPRE a la vista y nunca puede quedar inerte,
   aunque el estado `open` —que ahí no significa nada— siga en `false`. */
const MOVIL = '(max-width: 900px)';

function useEsMovil(): boolean {
  const [es, setEs] = useState(() => window.matchMedia(MOVIL).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOVIL);
    const onChange = () => setEs(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return es;
}

export function Shell({ crumbs, children }: { crumbs?: string[]; children: ReactNode }) {
  const { user, logout } = useSession();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const panel = useRef<HTMLElement | null>(null);
  const toggle = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);
  const esMovil = useEsMovil();

  // El drawer y el menú de perfil se cierran al navegar: si no, uno toca un
  // destino y se queda mirando el menú encima de la pantalla que pidió.
  useEffect(() => { setOpen(false); setMenuOpen(false); }, [location.pathname]);

  /** Cerrar el drawer por decisión de quien lo usa (Escape, scrim): el foco
   *  vuelve al botón que lo abrió. Al navegar no se hace — ahí lo que importa
   *  es la pantalla nueva, no el botón. */
  function cerrarDrawer() {
    setOpen(false);
    toggle.current?.focus();
  }

  // Y con Escape, que es lo que la gente prueba primero.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') cerrarDrawer(); };
    window.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Con el drawer abierto, la página de atrás no scrollea: en el teléfono el
  // dedo sobre el scrim movía el contenido de abajo, y al cerrar uno aparecía
  // en otra parte de la pantalla. Se restaura lo que hubiera (no un `''` a
  // ciegas) por si otra capa —un modal— ya lo había bloqueado.
  useEffect(() => {
    if (!open) return undefined;
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previo; };
  }, [open]);

  // El menú de perfil es chico y vive en una esquina: además de Escape, se
  // cierra al tocar cualquier lado afuera de él.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    const onClickOutside = (e: MouseEvent) => {
      if (menu.current && !menu.current.contains(e.target as Node)) setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [menuOpen]);

  // Los contadores viven en el panel, así que la barra lateral los reutiliza en
  // vez de pedir lo mismo otra vez.
  const dashboard = useApi<DashboardCounts>('/admin/dashboard', { staleTime: 60_000 });
  const counts = dashboard.data?.counts ?? {};

  return (
    <div className="app">
      {open && <div className="sidebar-scrim" onClick={cerrarDrawer} role="presentation" />}

      {/* `inert` solo con el drawer cerrado Y bajo 900px: el CSS lo saca de la
          vista (`visibility: hidden`), y esto lo saca del orden de tabulación
          y del árbol de accesibilidad — sin esto, Tab recorría diecisiete
          enlaces invisibles antes de llegar al contenido. En escritorio la
          barra siempre se ve y nunca es inerte. */}
      <aside
        className={`sidebar${open ? ' is-open' : ''}`}
        ref={panel}
        tabIndex={-1}
        aria-label="Navegación principal"
        inert={esMovil && !open}
      >
        <div className="sidebar__brand">
          {/* La marca oficial, el mismo componente que el portal y el ingreso.
              «Administración» es una etiqueta separada: no se pega al nombre ni
              forma «Clean BlueAdministración», y su separación es un
              `margin-top` explícito en CSS — antes lo único que separaba era el
              margen transparente del PNG, que ya no existe. */}
          <div className="brandmark">
            <Brandmark size={30} />
            <span className="sidebar__name">Clean Blue</span>
          </div>
          <div className="brandmark__label">Administración</div>
        </div>

        <nav id="sidebar-nav">
          {NAV.map((group) => (
            <div className="navgroup" key={group.label}>
              <div className="navgroup__label">{group.label}</div>
              {group.items.filter((item) => !item.adminOnly || user?.role === 'ADMIN').map((item) => {
                const badge = item.badge ? counts[item.badge] ?? 0 : 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/admin'}
                    className={({ isActive }) => `navlink${isActive ? ' is-active' : ''}`}
                  >
                    <span className="navlink__ico"><Icon name={item.icon} size={16} /></span>
                    {item.label}
                    {badge > 0 && <span className="navlink__badge">{badge}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      <div className="main">
        <header className="topbar">
          <button
            type="button"
            className="btn btn--ghost sidebar-toggle"
            ref={toggle}
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={open}
            aria-controls="sidebar-nav"
          >
            <Icon name="menu" size={20} />
          </button>

          <div className="crumbs">
            {(crumbs ?? []).map((crumb, i) => (
              <span key={`${crumb}-${location.pathname}`}>
                {i > 0 && <span className="sep"> / </span>}
                {crumb}
              </span>
            ))}
          </div>

          <div className="topbar__spacer" />

          <div className="topbar__user profile-menu" ref={menu}>
            <button
              type="button"
              className="profile-menu__trigger"
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              {/* La foto propia sale de `/api/me/photo`, no de la ruta de
                  administración: es la misma imagen, pero pedirla por «la
                  mía» en vez de por «la del perfil tal» no depende de tener
                  permiso para ver perfiles ajenos. */}
              <Avatar
                src={user?.photo_key ? `/api/me/photo?v=${user.photo_key}` : null}
                name={user?.full_name ?? '?'}
              />
              <span className="small">{user?.full_name}</span>
            </button>

            {/* Dos ítems, no tres: Configuración se fue al sidebar (son datos
                del negocio, no de la persona) y «Mi foto» creció a «Mi perfil»
                —la foto es un dato del perfil, no el perfil—.

                Los íconos son decorativos (`aria-hidden`, que es el default de
                `Icon`): el nombre accesible sale del texto de al lado, y
                anunciarlo dos veces solo estorba con lector de pantalla. */}
            {menuOpen && (
              <div className="profile-menu__panel" role="menu">
                <NavLink
                  to="/admin/perfil"
                  role="menuitem"
                  className={({ isActive }) => `profile-menu__item${isActive ? ' is-active' : ''}`}
                >
                  <Icon name="perfil" size={16} />
                  Mi perfil
                </NavLink>
                <button
                  type="button"
                  role="menuitem"
                  className="profile-menu__item"
                  onClick={() => void logout()}
                >
                  <Icon name="salir" size={16} />
                  Salir
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="content">{children}</main>
      </div>
    </div>
  );
}
